// frontend/src/components/auth/CambiarPasswordScreen.tsx
// Cambio de contraseña obligatorio: primer inicio de sesión o contraseña
// restablecida por un administrador. Mismo estilo que LoginScreen.

import { useState } from 'react';
import { AlertCircle, Check, Eye, EyeOff, KeyRound, Lock, LogOut, RefreshCw, ShieldCheck } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { api, type UsuarioSesion } from '../../lib/api';
import { DUR } from '../../lib/motion';

// Mismas reglas que el servidor (authController.cambiarPassword)
export const MIN_LONGITUD_PASSWORD = 6;

interface CambiarPasswordScreenProps {
  usuario: UsuarioSesion;
  onCambiada: (token: string, user: UsuarioSesion) => void;
  onLogout: () => void;
}

const estiloInput = {
  fontFamily: 'Inter, sans-serif',
  backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
  borderColor: 'var(--color-sgcc-outline-variant)',
  color: 'var(--color-sgcc-on-surface)',
} as const;

function CampoPassword({
  id, label, value, onChange, disabled, autoComplete, autoFocus, Icono,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  autoComplete: string;
  autoFocus?: boolean;
  Icono: typeof Lock;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label
        className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
        style={{ color: 'var(--color-sgcc-outline)' }}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="relative">
        <Icono size={20} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-sgcc-outline)' }} />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className="w-full pl-10 pr-12 h-12 text-sm border rounded-lg outline-none transition-all"
          style={estiloInput}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-sgcc-primary)';
            e.target.style.boxShadow = '0 0 0 2px rgba(0, 30, 64, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--color-sgcc-outline-variant)';
            e.target.style.boxShadow = 'none';
          }}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-md tap-active"
          style={{ color: 'var(--color-sgcc-outline)' }}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          {visible ? <EyeOff size={18} strokeWidth={1.8} /> : <Eye size={18} strokeWidth={1.8} />}
        </button>
      </div>
    </div>
  );
}

export function CambiarPasswordScreen({ usuario, onCambiada, onLogout }: CambiarPasswordScreenProps) {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reglas = [
    { ok: nueva.length >= MIN_LONGITUD_PASSWORD, texto: `Al menos ${MIN_LONGITUD_PASSWORD} caracteres` },
    { ok: !!nueva && nueva.toLowerCase() !== usuario.usuario.toLowerCase(), texto: 'Distinta de su nombre de usuario' },
    { ok: !!nueva && nueva !== actual, texto: 'Distinta de la contraseña actual' },
    { ok: !!nueva && nueva === confirmacion, texto: 'Las dos contraseñas nuevas coinciden' },
  ];
  const valida = !!actual && reglas.every((r) => r.ok);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valida) return; // el botón ya está deshabilitado; Enter no debe enviar
    setLoading(true);
    setError(null);
    try {
      const res = await api.cambiarPassword(actual, nueva);
      if (res.success && res.data) {
        onCambiada(res.data.token, res.data.user);
      } else {
        setError(res.error || 'No se pudo cambiar la contraseña.');
      }
    } catch (err: any) {
      setError(err?.message === 'NETWORK_ERROR'
        ? 'Sin conexión con el servidor. Necesita conexión para cambiar su contraseña.'
        : err?.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col justify-center items-center p-4 animate-fade-in"
      style={{ backgroundColor: 'var(--color-sgcc-surface)', fontFamily: 'Inter, sans-serif' }}
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <div
            className="w-16 h-16 rounded-[20px] flex items-center justify-center mb-3"
            style={{ backgroundColor: 'var(--color-sgcc-primary)' }}
          >
            <KeyRound size={30} strokeWidth={1.8} style={{ color: '#fed65b' }} />
          </div>
          <p className="text-lg font-bold" style={{ color: 'var(--color-sgcc-primary)' }}>
            Hola, {usuario.nombres.split(' ')[0]}
          </p>
          <p className="text-sm text-center mt-1 px-4" style={{ color: 'var(--color-sgcc-on-surface-variant)' }}>
            Por seguridad, elija una contraseña nueva que pueda recordar antes de continuar.
          </p>
        </div>

        <div
          className="rounded-lg border overflow-hidden"
          style={{
            backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
            borderColor: 'var(--color-sgcc-outline-variant)',
          }}
        >
          <div
            className="px-6 py-4 border-b"
            style={{ backgroundColor: 'var(--color-sgcc-surface-container)', borderColor: 'var(--color-sgcc-outline-variant)' }}
          >
            <p className="font-semibold text-sm uppercase tracking-wider" style={{ color: 'var(--color-sgcc-primary)' }}>
              Cambiar contraseña
            </p>
          </div>

          <div className="p-6">
            <form id="cambiar-password-form" onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <m.div
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: [0, -8, 8, -5, 5, -2, 0] }}
                  transition={{ opacity: { duration: DUR.rapida }, x: { duration: 0.42, ease: 'easeOut' } }}
                  className="flex items-start gap-2.5 p-3 rounded-lg border text-sm"
                  style={{
                    backgroundColor: 'var(--color-sgcc-error-container)',
                    borderColor: 'rgba(186, 26, 26, 0.3)',
                    color: 'var(--color-sgcc-on-error-container)',
                  }}
                >
                  <AlertCircle size={18} strokeWidth={2} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </m.div>
              )}

              {/* Campo oculto para que el gestor de contraseñas asocie la nueva al usuario */}
              <input type="text" name="username" autoComplete="username" value={usuario.usuario} readOnly hidden />

              <CampoPassword id="pwd-actual" label="Contraseña actual (la que le dieron)" value={actual} onChange={setActual}
                disabled={loading} autoComplete="current-password" autoFocus Icono={Lock} />
              <CampoPassword id="pwd-nueva" label="Nueva contraseña" value={nueva} onChange={setNueva}
                disabled={loading} autoComplete="new-password" Icono={KeyRound} />
              <CampoPassword id="pwd-confirmacion" label="Repetir nueva contraseña" value={confirmacion} onChange={setConfirmacion}
                disabled={loading} autoComplete="new-password" Icono={KeyRound} />

              {/* Reglas en vivo */}
              <ul className="space-y-1.5 pt-1">
                {reglas.map((r) => (
                  <li key={r.texto} className="flex items-center gap-2 text-[12.5px]"
                    style={{ color: r.ok ? '#1a6f3c' : 'var(--color-sgcc-outline)' }}>
                    <m.span
                      className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                      animate={{ backgroundColor: r.ok ? '#34C759' : 'rgba(60,60,67,0.12)', scale: r.ok ? [1, 1.25, 1] : 1 }}
                      transition={{ duration: DUR.base }}
                    >
                      {r.ok && <Check size={11} strokeWidth={3} color="white" />}
                    </m.span>
                    {r.texto}
                  </li>
                ))}
              </ul>

              <button
                id="cambiar-password-submit"
                type="submit"
                disabled={loading || !valida}
                className="w-full h-12 rounded-lg font-semibold text-sm uppercase tracking-wider transition-all tap-active flex items-center justify-center gap-2 mt-2"
                style={{
                  backgroundColor: loading || !valida ? 'var(--color-sgcc-outline-variant)' : 'var(--color-sgcc-primary)',
                  color: 'var(--color-sgcc-on-primary)',
                  cursor: loading || !valida ? 'not-allowed' : 'pointer',
                }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <m.span
                    key={loading ? 'cargando' : 'listo'}
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6, transition: { duration: DUR.rapida } }}
                  >
                    {loading ? (
                      <><RefreshCw size={20} strokeWidth={2} className="animate-spin-slow" /><span>Guardando...</span></>
                    ) : (
                      <><ShieldCheck size={20} strokeWidth={2} /><span>Guardar y continuar</span></>
                    )}
                  </m.span>
                </AnimatePresence>
              </button>
            </form>
          </div>
        </div>

        <button
          type="button"
          onClick={onLogout}
          className="w-full mt-4 h-11 flex items-center justify-center gap-2 text-sm font-medium tap-active"
          style={{ color: 'var(--color-sgcc-outline)' }}
        >
          <LogOut size={16} strokeWidth={1.8} />
          Salir sin cambiarla
        </button>
      </div>
    </div>
  );
}
